import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatWeight } from '@vj/shared';
import { api, downloadFile } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

const ISSUED_TO_LABEL = { karigar: 'Aasari', employee: 'Employee', customer: 'Customer' };

export default function PendingIssuesPage() {
  const { data: rows, isLoading } = useQuery({
    queryKey: ['reports', 'pending-issues'],
    queryFn: () => api.get('/reports/pending-issues'),
  });
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile('/reports/exports/pending-issues', 'pending-issues.csv');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Pending issues</h1>
          <p className="mt-1 text-sm text-muted">Items out with karigars/aasaris that haven&rsquo;t come back yet.</p>
        </div>
        <Button variant="secondary" size="sm" loading={downloading} onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> CSV
        </Button>
      </div>

      <div className="card table-scroll">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Voucher</th>
              <th className="px-4 py-3">Issued to</th>
              <th className="px-4 py-3">Purpose</th>
              <th className="px-4 py-3 text-right">Net wt.</th>
              <th className="px-4 py-3 text-right">Days out</th>
              <th className="px-4 py-3">Expected return</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
            ) : !rows?.length ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">Nothing outstanding.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.voucher_no} className="tabular border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium">{r.voucher_no}</td>
                  <td className="px-4 py-3">
                    {r.issued_to_name}
                    <div className="text-xs text-muted">{ISSUED_TO_LABEL[r.issued_to_type] ?? r.issued_to_type}</div>
                  </td>
                  <td className="px-4 py-3 text-muted">{r.purpose || '—'}</td>
                  <td className="px-4 py-3 text-right">{formatWeight(r.total_net_weight)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={Number(r.daysOutstanding) > 14 ? 'font-semibold text-danger' : ''}>{r.daysOutstanding}</span>
                  </td>
                  <td className="px-4 py-3 text-muted">{r.expected_return_date || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
